import React from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Flex } from '@atlassian/eoc-flex';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import {
	commonMessages as scheduleCommonMessages,
	useOpenWebCalendar,
	Wrap,
} from '@atlassian/eoc-schedule-common';

import { SPOTLIGHT_NAMES } from '../../../../common/constants';
import { messages as commonMessages } from '../../../../common/messages';
import { usePartialUpdateScheduleService } from '../../../../controllers/list';
import { useActions as usePageActions } from '../../../../controllers/page-store';
import { useCollapseUiPreference } from '../../../../controllers/preferences';
import { useCompleteTask, useExportTimelineService } from '../../../../controllers/schedule';
import { useHasDeleted, useSchedule } from '../../../../controllers/schedule-store/helpers';
import { getScheduleRightsResource } from '../../../../services/resources';

import { messages } from './messages';
import { Container } from './styled';
import { type HeaderProps } from './types';

const Header = ({ index }: HeaderProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intlApi = useIntl();
	const [schedule] = useSchedule();

	const [hasDeleted] = useHasDeleted();
	const { openEditMode, openDeleteSchedule, openCloneSchedule } = usePageActions();
	const { isLoading: isExporting, exportTimeline } = useExportTimelineService(schedule.id);
	const { isLoading: isOpenWebCalendarLoading, openWebCalendar } = useOpenWebCalendar(schedule.id);
	const { partialUpdate: updateEnabled, isLoading: isEnabledLoading } =
		usePartialUpdateScheduleService(schedule.id, 'enabled');
	const { isCollapsed, toggleCollapsePreference } = useCollapseUiPreference({
		id: schedule.id,
	});
	const { completeTask } = useCompleteTask();
	const { data: scheduleRights } = useResource(getScheduleRightsResource);

	const isSettingsLoading = isEnabledLoading || isExporting || isOpenWebCalendarLoading;

	return (
		<Container>
			<Flex columnGap={1} justifyContent="center">
				{hasDeleted && (
					<Lozenge appearance="removed">
						{intlApi.formatMessage(scheduleCommonMessages.missingResponder)}
					</Lozenge>
				)}

				{!schedule.enabled && (
					<Lozenge appearance="moved">{intlApi.formatMessage(scheduleCommonMessages.off)}</Lozenge>
				)}
			</Flex>

			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<Wrap
						within={(e) => (
							<SpotlightTarget name={SPOTLIGHT_NAMES.firstScheduleActions}>{e}</SpotlightTarget>
						)}
						when={index === 0}
					>
						<Box paddingInlineStart="space.100">
							<IconButton
								{...props}
								id={schedule.id}
								isLoading={isSettingsLoading}
								appearance="subtle"
								icon={MoreIcon}
								label={intlApi.formatMessage(messages.moreActionsForSchedule, {
									scheduleName: schedule.name,
								})}
								ref={triggerRef}
							/>
						</Box>
					</Wrap>
				)}
			>
				{scheduleRights!.hasEditRight && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'editSchedule');

							openEditMode({
								schedule,
								id: [schedule.id, 'create-schedule-button'],
							});

							completeTask();
						}}
					>
						{intlApi.formatMessage(commonMessages.edit)}
					</DropdownItem>
				)}

				{scheduleRights!.hasEditRight && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'cloneSchedule');

							openCloneSchedule({
								schedule,
								id: [schedule.id, 'create-schedule-button'],
							});
						}}
					>
						{intlApi.formatMessage(commonMessages.duplicate)}
					</DropdownItem>
				)}

				{fg('show-schedule-add-to-calendar-button') && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'addToCalendarSchedule');

							openWebCalendar();
						}}
					>
						{intlApi.formatMessage(scheduleCommonMessages.addToCalendar)}
					</DropdownItem>
				)}

				<DropdownItem
					onClick={() => {
						const event = createAnalyticsEvent({});
						fireUIAnalytics(event, 'button clicked', 'exportSchedule');

						exportTimeline();
					}}
				>
					{intlApi.formatMessage(messages.export)}
				</DropdownItem>

				{schedule.enabled && scheduleRights!.hasEditRight && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'disableSchedule');

							updateEnabled(false);
						}}
					>
						{intlApi.formatMessage(commonMessages.turnOff)}
					</DropdownItem>
				)}
				{!schedule.enabled && scheduleRights!.hasEditRight && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'enableSchedule');

							updateEnabled(true);
						}}
					>
						{intlApi.formatMessage(commonMessages.turnOn)}
					</DropdownItem>
				)}

				{scheduleRights!.hasDeleteRight && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'deleteSchedule');

							openDeleteSchedule({
								schedule,
								id: [schedule.id, 'create-schedule-button'],
							});
						}}
					>
						{intlApi.formatMessage(commonMessages.delete)}
					</DropdownItem>
				)}

				<DropdownItemGroup hasSeparator>
					<DropdownItem onClick={toggleCollapsePreference}>
						{isCollapsed
							? intlApi.formatMessage(messages.expand)
							: intlApi.formatMessage(messages.collapse)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</Container>
	);
};

export { Header };
